import type { Lang } from './types';

export const LANGUAGES: { id: Lang; label: string; short: string }[] = [
  { id: 'en', label: 'English', short: 'EN' },
  { id: 'hi', label: 'हिंदी', short: 'हि' },
  { id: 'gu', label: 'ગુજરાતી', short: 'ગુ' },
];

const en = {
  // navigation
  nav_dashboard: 'Dashboard',
  nav_menu: 'Menu',
  nav_order: 'New Order',
  nav_orders: 'Orders',
  nav_offers: 'Offers',
  nav_analytics: 'Analytics',

  // screen headers
  title_dashboard: 'Dashboard',
  tag_dashboard: "Today's snapshot",
  title_menu: 'Menu Management',
  tag_menu: 'Browse, price and tag items by category',
  title_order: 'New Order',
  tag_order: 'Pick a table, then add items',
  title_orders: 'Order History',
  tag_orders: 'Every order placed this session',
  title_offers: 'Offers',
  tag_offers: 'Run % discounts by item or whole category',
  title_analytics: 'Analytics',
  tag_analytics: "What's actually selling",

  // dashboard
  stat_items: 'Menu items',
  stat_categories: 'Categories',
  stat_on_offer: 'Items on offer',
  stat_open_tables: 'Open tables',
  stat_orders: 'Orders placed',
  stat_revenue: 'Revenue',
  top_sellers: 'Top sellers',
  top_sellers_hint: 'Place an order to see this move',
  recent_orders: 'Recent orders',
  running_tables: 'Running tables',
  no_orders_yet: 'No orders placed yet.',
  no_running_tables: 'No tables are open right now.',
  no_sales_yet: 'No sales yet — place an order to populate this.',

  // order flow
  step_table: 'Table',
  step_category: 'Category',
  step_items: 'Items',
  select_table: 'Select a table',
  select_table_hint: 'Each table keeps its own running order',
  table: 'Table',
  table_free: 'Free',
  parcel: 'Parcel',
  add_table: 'Add table',
  add_table_title: 'Add a table number',
  add_table_hint: 'Enter any table number or a short name.',
  select_category: 'Select a category',
  select_category_hint: 'Tap a category to see its items',
  back: 'Back',
  view_cart: 'View order',
  cart: 'Current order',
  empty_cart: 'Nothing added yet. Go back and pick a category.',
  place_order: 'Place Order',
  clear_table: 'Clear table',
  customer_details: 'Customer details',
  optional: 'optional',
  customer_name: 'Name',
  customer_phone: 'Phone number',
  walk_in: 'Walk-in',
  items_in_order: 'in order',

  // money
  subtotal: 'Subtotal',
  discount: 'Discount',
  item_savings: 'Item offers saved',
  total: 'Total',
  rate: 'Rate',
  qty_short: 'Qty',
  amount: 'Amount',

  // offers
  category_offer_title: 'Apply an offer to a whole category',
  category_offer_hint: 'Great for slow-hour promos',
  items_on_offer: 'Items currently on offer',
  clear_all_offers: 'Clear all offers',
  no_offers: 'No active offers. Set one above, or from any item in the Menu tab.',
  off: 'OFF',
  custom: 'Custom',
  clear: 'Clear',
  custom_discount_for: 'Custom discount for "{name}"',
  discount_range_hint: 'Enter a discount between 0 and {max}.',
  cancel: 'Cancel',
  apply: 'Apply',
  save: 'Save',

  // order history
  order_no: 'Order #{n}',
  order_details: 'Order details',
  no_history: 'No orders yet this session.',
  placed_at: 'Placed at',
  items_label: 'Items',
  close: 'Close',
  base_price: 'Menu price',

  // analytics
  best_selling: 'Best-selling items',
  by_category: 'By category',
  no_analytics: 'Place a few orders to see rankings here.',
  orders_word: 'orders',
  items_on_menu: 'items on menu',

  // common
  search_placeholder: 'Search a menu item…',
  no_match: 'No items match.',
  language: 'Language',
  text_size: 'Text size',
  settings: 'Settings',
  appearance: 'Appearance',
  size_sm: 'Small',
  size_md: 'Medium',
  size_lg: 'Large',
  size_xl: 'Extra large',
  size_preview: 'Paneer Chilli Dry · ₹199',
  sign_in: 'Sign in',
  sign_out: 'Sign out',
  email: 'Email',
  password: 'Password',
  signing_in: 'Signing in…',
  sign_in_subtitle: 'Sign in so this device shares orders with the others.',
  work_offline: 'Work offline on this device',
  sync_synced: 'All saved',
  sync_syncing: 'Saving…',
  sync_offline: 'Offline',
  sync_error: 'Not saved',
  sync_signed_out: 'Not signed in',
  sync_no_access: 'No access',
  sync_pending: '{n} waiting',
  sync_now: 'Sync now',
  not_synced: 'Not uploaded yet',
  account: 'Account',
  browse: 'Browse',
  manage: 'Manage',
  add_category: 'Add category',
  edit_category: 'Edit category',
  add_item: 'Add item',
  edit_item: 'Edit item',
  item_name: 'Item name',
  category_name: 'Category name',
  name_en: 'Name (English)',
  name_hi: 'Name (Hindi)',
  name_gu: 'Name (Gujarati)',
  icon: 'Icon',
  prices_label: 'Prices',
  variant_label: 'Label',
  price_label: 'Price',
  add_variant: 'Add price option',
  variant_hint: 'Use "Price" for a single price, or add Half / Full, Oil / Butter.',
  delete: 'Delete',
  delete_category_confirm: 'Delete this category and all its items?',
  delete_item_confirm: 'Delete this item?',
  active_label: 'Available',
  hidden_label: 'Hidden',
  no_items_here: 'No items in this category yet.',
  saved_toast: 'Saved',
  deleted_toast: 'Deleted',
  name_required: 'Enter a name',
  price_required: 'Add at least one price',
  theme: 'Appearance',
  theme_light: 'Light',
  theme_dark: 'Dark',
  all: 'All',
  items_n: '{n} items',
  item_1: '1 item',
  added_toast: 'Added {name}',
  order_placed_toast: 'Order #{n} placed · {total}',
  offer_set_toast: '{name}: {pct}% offer set',
  offer_cleared_toast: '{name}: offer cleared',
  all_offers_cleared_toast: 'All offers cleared',
  cat_offer_toast: '{name}: {pct}% applied to all items',
  table_cleared_toast: '{name} cleared',
  customer_saved_toast: 'Customer details saved',

  // categories
  cat_special: 'Luck Special',
  cat_pavbhaji: 'Pavbhaji',
  cat_pulao: 'Pulao',
  cat_dosa: 'Dosa · Paper',
  cat_rollcut: 'Roll Cut Dosa',
  cat_premroll: 'Special Roll Cut',
  cat_soup: 'Soup',
  cat_manchurian: 'Manchurian',
  cat_rice: 'Rice',
  cat_noodles: 'Noodles',
  cat_bhel: 'Bhel',
  cat_chinese: 'Premium Chinese',
  cat_drinks: 'Cold Drinks',
  // floor plan + restaurant settings
  tables_tab: 'Tables',
  add_floor_table: 'Add table',
  edit_floor_table: 'Edit table',
  table_id: 'Table number or code',
  table_id_hint: 'A number reads as "Table 4". A word is used as-is.',
  table_label: 'Display name',
  table_label_hint: 'Leave empty to use the number.',
  seats: 'Seats',
  table_kind: 'Type',
  kind_dine_in: 'Dine-in',
  kind_parcel: 'Parcel',
  delete_table_confirm: 'Tap again to delete',
  id_required: 'Enter a table number or name.',
  id_taken: 'That table already exists.',
  restaurant: 'Restaurant',
  brand_name: 'Restaurant name',
  brand_tagline: 'Tagline',
  address: 'Address',
  gstin: 'GSTIN',
  discount_presets: 'Quick discounts',
  discount_presets_hint: 'Comma separated, e.g. 10, 20, 25',
  max_discount: 'Maximum discount %',
  no_tables_yet: 'No tables yet. Add one to start taking orders.',
  // printing
  print: 'Print',
  print_bill: 'Print bill',
  printing: 'Printing…',
  print_failed: 'Could not print on this device',
  invoice: 'Invoice',
  gst_note: 'Prices are inclusive of GST. GST is not charged separately.',
  invoice_thanks: 'Thank you — please visit again',
  // analytics periods
  period_day: 'Today',
  period_week: 'This week',
  period_month: 'This month',
  revenue_over_time: 'Revenue over time',
  orders_in_period: 'Orders',
  items_in_period: 'Items sold',
  avg_order: 'Average order',
  no_sales_in_period: 'Nothing sold in this period yet',
  // staff
  no_open_orders: 'Nothing waiting',
  nav_admin: 'Admin',
  title_admin: 'Admin',
  tag_admin: 'Restaurants, staff and roles',
  admin_web_only: 'Admin is managed from the web app.',
  admin_owner_only: 'Only an owner can manage restaurants and staff.',
  admin_admin_only: 'Only the administrator can manage restaurants and owners.',
  nav_staff: 'Staff',
  title_staff: 'Staff',
  tag_staff: 'The workers at this restaurant',
  staff_web_only: 'Staff are managed from the web app.',
  staff_owner_only: 'Only an owner can manage workers.',
  workers: 'Workers',
  add_worker: 'Add worker',
  no_workers: 'No workers yet. Add one and give them their first password.',
  owner_appointed_by_admin: 'Owners are appointed by the administrator.',
  restaurants: 'Restaurants',
  add_restaurant: 'Add restaurant',
  edit_restaurant: 'Edit restaurant',
  restaurant_name: 'Restaurant name',
  staff: 'Staff',
  add_staff: 'Add staff',
  edit_staff: 'Edit staff',
  staff_email: 'Email',
  staff_role: 'Role',
  role_admin: 'Administrator',
  role_owner: 'Owner',
  role_worker: 'Worker',
  role_owner_hint: 'Everything, including managing staff and restaurant details.',
  role_worker_hint: 'Takes and edits orders and the menu. Cannot manage staff.',
  invite_pending: 'Waiting for first sign-in',
  email_required: 'Enter an email address.',
  email_taken: 'That email is already on the team.',
  remove_staff_confirm: 'Tap again to remove',
  switch_restaurant: 'Switch restaurant',
  no_access: 'This account is not on any restaurant yet',
  no_access_hint: 'Ask the owner to add {email} from Admin → Staff.',
  // account setup, logout, customers, date range
  confirm_password: 'Confirm password',
  passwords_differ: 'The two passwords do not match.',
  password_too_short: 'Use at least 6 characters.',

  // Nothing here sends mail. An owner types the first password in Admin and
  // hands it over in person; forgetting it means being given another one.
  forgot_password: 'Forgot your password?',
  forgot_password_hint:
    'Ask an owner to set you a new one from Admin → Staff. They can hand it to you straight away.',
  staff_password: 'First password',
  staff_password_hint:
    'You choose this and tell them. They sign in with it, and the app then offers to let them pick their own.',
  staff_password_new: 'New password',
  staff_password_reset_hint:
    'Leave blank to keep their current password. Anything you type here replaces it immediately.',
  set_password: 'Set password',
  password_set_toast: 'New password set',
  staff_needs_online: 'Connect to the internet to add staff or change a password.',
  staff_login_exists:
    'Added. That address already had a login, so they keep their existing password — the one you typed was not used.',
  change_password_title: 'Change password',
  change_password_hint:
    'Enter the password you use now, then the new one twice. Nothing is emailed.',
  current_password: 'Current password',
  current_password_required: 'Enter the password you use now.',
  current_password_wrong: 'That is not your current password.',
  password_unchanged: 'The new password is the same as the current one.',
  password_changed_toast: 'Password updated',
  signed_in_as: 'Signed in as',
  account_worker: 'Worker',
  nav_customers: 'Customers',
  title_customers: 'Customers',
  tag_customers: 'Who came, when, and what they spent',
  no_customers: 'No customer details captured yet',
  no_customers_hint: 'Add a name or phone on the order review step and they appear here.',
  search_customers: 'Search name or phone…',
  visits: '{n} visits',
  visit_1: '1 visit',
  last_visit: 'Last visit',
  first_visit: 'First visit',
  total_spent: 'Total spent',
  customer_orders: 'Their orders',
  from_date: 'From',
  to_date: 'To',
  period_custom: 'Date range',
  date_hint: 'YYYY-MM-DD',
  bad_date: 'Use YYYY-MM-DD, e.g. {example}.',
  last_7: 'Last 7 days',
  last_30: 'Last 30 days',
  apply_range: 'Apply',

  nav_more: 'More',
  title_more: 'More',
  tag_more: 'Everything else the app can do',
  // ---- money out (owner only) ----
  nav_expenses: 'Expenses',
  title_expenses: 'Expenses',
  tag_expenses: 'What the shop is paying out',
  // spending heads
  exp_salary: 'Staff salary',
  exp_rent: 'Rent',
  exp_material: 'Raw material',
  exp_vegetables: 'Vegetables',
  exp_utilities: 'Gas & power',
  exp_maintenance: 'Maintenance',
  exp_other: 'Other',
  // figures
  spent_in_period: 'Spent',
  revenue_in_period: 'Revenue',
  net_profit: 'Net profit',
  net_loss: 'Net loss',
  margin_pct: '{pct}% margin',
  no_margin_yet: 'No sales in this period',
  entries_in_period: 'Entries',
  spend_over_time: 'Spending over time',
  by_head: 'Where the money went',
  all_entries: 'Every entry',
  no_spend_in_period: 'Nothing recorded in this period yet',
  no_heads_yet: 'No spending heads yet',
  entries_word: 'entries',
  entry_1: '1 entry',
  uncategorised: 'Uncategorised',
  // the editor
  add_expense: 'Add an expense',
  edit_expense: 'Edit expense',
  expense_amount: 'Amount paid',
  expense_head: 'What for',
  expense_payee: 'Paid to',
  expense_payee_hint: 'A name, a shop, a person — whatever helps you find it later.',
  expense_note: 'Note',
  expense_date: 'Date paid',
  expense_date_hint: 'When the money actually left, not when you typed it in.',
  expense_amount_required: 'Enter an amount.',
  expense_head_required: 'Pick what it was for.',
  expense_date_required: 'Enter the date it was paid.',
  expense_saved_toast: '{amount} recorded',
  delete_expense_confirm: 'Tap again to delete',
  // the heads editor
  manage_heads: 'Spending heads',
  manage_heads_hint: 'Rename these, or add your own',
  add_head: 'Add a head',
  edit_head: 'Edit head',
  head_name: 'Name',
  delete_head_confirm: 'Tap again — entries are kept',
  head_name_required: 'Give it a name.',
  head_name_taken: 'There is already a head with that name.',
  // the gate
  expenses_owner_only: 'Only an owner can see what the shop spends.',
};

export type StringKey = keyof typeof en;

const hi: Record<StringKey, string> = {
  nav_dashboard: 'डैशबोर्ड',
  nav_menu: 'मेन्यू',
  nav_order: 'नया ऑर्डर',
  nav_orders: 'ऑर्डर',
  nav_offers: 'ऑफर',
  nav_analytics: 'रिपोर्ट',

  title_dashboard: 'डैशबोर्ड',
  tag_dashboard: 'आज का सारांश',
  title_menu: 'मेन्यू प्रबंधन',
  tag_menu: 'श्रेणी के अनुसार आइटम देखें और ऑफर लगाएँ',
  title_order: 'नया ऑर्डर',
  tag_order: 'पहले टेबल चुनें, फिर आइटम जोड़ें',
  title_orders: 'ऑर्डर इतिहास',
  tag_orders: 'इस सत्र के सभी ऑर्डर',
  title_offers: 'ऑफर',
  tag_offers: 'आइटम या पूरी श्रेणी पर छूट लगाएँ',
  title_analytics: 'रिपोर्ट',
  tag_analytics: 'सबसे ज़्यादा क्या बिक रहा है',

  stat_items: 'मेन्यू आइटम',
  stat_categories: 'श्रेणियाँ',
  stat_on_offer: 'ऑफर वाले आइटम',
  stat_open_tables: 'चालू टेबल',
  stat_orders: 'कुल ऑर्डर',
  stat_revenue: 'कुल आमदनी',
  top_sellers: 'सबसे ज़्यादा बिकने वाले',
  top_sellers_hint: 'ऑर्डर करें और यहाँ बदलाव देखें',
  recent_orders: 'हाल के ऑर्डर',
  running_tables: 'चालू टेबल',
  no_orders_yet: 'अभी तक कोई ऑर्डर नहीं।',
  no_running_tables: 'अभी कोई टेबल चालू नहीं है।',
  no_sales_yet: 'अभी कोई बिक्री नहीं — एक ऑर्डर करें।',

  step_table: 'टेबल',
  step_category: 'श्रेणी',
  step_items: 'आइटम',
  select_table: 'टेबल चुनें',
  select_table_hint: 'हर टेबल का अपना ऑर्डर अलग चलता है',
  table: 'टेबल',
  table_free: 'खाली',
  parcel: 'पार्सल',
  add_table: 'टेबल जोड़ें',
  add_table_title: 'टेबल नंबर जोड़ें',
  add_table_hint: 'कोई भी टेबल नंबर या छोटा नाम लिखें।',
  select_category: 'श्रेणी चुनें',
  select_category_hint: 'आइटम देखने के लिए श्रेणी दबाएँ',
  back: 'वापस',
  view_cart: 'ऑर्डर देखें',
  cart: 'मौजूदा ऑर्डर',
  empty_cart: 'अभी कुछ नहीं जोड़ा। वापस जाकर श्रेणी चुनें।',
  place_order: 'ऑर्डर करें',
  clear_table: 'टेबल खाली करें',
  customer_details: 'ग्राहक विवरण',
  optional: 'वैकल्पिक',
  customer_name: 'नाम',
  customer_phone: 'फ़ोन नंबर',
  walk_in: 'बिना नाम',
  items_in_order: 'ऑर्डर में',

  subtotal: 'उप-योग',
  discount: 'छूट',
  item_savings: 'आइटम ऑफर से बचत',
  total: 'कुल',
  rate: 'भाव',
  qty_short: 'मात्रा',
  amount: 'राशि',

  category_offer_title: 'पूरी श्रेणी पर ऑफर लगाएँ',
  category_offer_hint: 'कम भीड़ के समय के लिए बढ़िया',
  items_on_offer: 'अभी ऑफर वाले आइटम',
  clear_all_offers: 'सभी ऑफर हटाएँ',
  no_offers: 'कोई ऑफर चालू नहीं। ऊपर से या मेन्यू टैब से लगाएँ।',
  off: 'छूट',
  custom: 'अपना',
  clear: 'हटाएँ',
  custom_discount_for: '"{name}" के लिए अपनी छूट',
  discount_range_hint: '0 से {max} के बीच छूट लिखें।',
  cancel: 'रद्द करें',
  apply: 'लगाएँ',
  save: 'सहेजें',

  order_no: 'ऑर्डर #{n}',
  order_details: 'ऑर्डर विवरण',
  no_history: 'इस सत्र में अभी कोई ऑर्डर नहीं।',
  placed_at: 'समय',
  items_label: 'आइटम',
  close: 'बंद करें',
  base_price: 'मेन्यू भाव',

  best_selling: 'सबसे ज़्यादा बिकने वाले आइटम',
  by_category: 'श्रेणी के अनुसार',
  no_analytics: 'कुछ ऑर्डर करें, फिर यहाँ क्रम दिखेगा।',
  orders_word: 'ऑर्डर',
  items_on_menu: 'आइटम मेन्यू में',

  search_placeholder: 'मेन्यू आइटम खोजें…',
  no_match: 'कोई आइटम नहीं मिला।',
  language: 'भाषा',
  text_size: 'अक्षर का आकार',
  settings: 'सेटिंग',
  appearance: 'दिखावट',
  size_sm: 'छोटा',
  size_md: 'सामान्य',
  size_lg: 'बड़ा',
  size_xl: 'बहुत बड़ा',
  size_preview: 'पनीर चिली ड्राय · ₹199',
  sign_in: 'साइन इन',
  sign_out: 'साइन आउट',
  email: 'ईमेल',
  password: 'पासवर्ड',
  signing_in: 'साइन इन हो रहा है…',
  sign_in_subtitle: 'साइन इन करें ताकि यह डिवाइस बाकी से ऑर्डर साझा करे।',
  work_offline: 'इसी डिवाइस पर ऑफ़लाइन चलाएँ',
  sync_synced: 'सब सेव है',
  sync_syncing: 'सेव हो रहा है…',
  sync_offline: 'ऑफ़लाइन',
  sync_error: 'सेव नहीं हुआ',
  sync_signed_out: 'साइन इन नहीं',
  sync_no_access: 'पहुँच नहीं',
  sync_pending: '{n} बाकी',
  sync_now: 'अभी सिंक करें',
  not_synced: 'अभी अपलोड नहीं हुआ',
  account: 'खाता',
  browse: 'देखें',
  manage: 'प्रबंधन',
  add_category: 'श्रेणी जोड़ें',
  edit_category: 'श्रेणी बदलें',
  add_item: 'आइटम जोड़ें',
  edit_item: 'आइटम बदलें',
  item_name: 'आइटम का नाम',
  category_name: 'श्रेणी का नाम',
  name_en: 'नाम (अंग्रेज़ी)',
  name_hi: 'नाम (हिंदी)',
  name_gu: 'नाम (गुजराती)',
  icon: 'आइकन',
  prices_label: 'भाव',
  variant_label: 'नाम',
  price_label: 'भाव',
  add_variant: 'भाव विकल्प जोड़ें',
  variant_hint: 'एक ही भाव हो तो "Price" रखें, या Half / Full, Oil / Butter जोड़ें।',
  delete: 'हटाएँ',
  delete_category_confirm: 'यह श्रेणी और इसके सभी आइटम हटाएँ?',
  delete_item_confirm: 'यह आइटम हटाएँ?',
  active_label: 'उपलब्ध',
  hidden_label: 'छिपा हुआ',
  no_items_here: 'इस श्रेणी में अभी कोई आइटम नहीं।',
  saved_toast: 'सहेजा गया',
  deleted_toast: 'हटाया गया',
  name_required: 'नाम लिखें',
  price_required: 'कम से कम एक भाव जोड़ें',
  theme: 'दिखावट',
  theme_light: 'उजला',
  theme_dark: 'गहरा',
  all: 'सभी',
  items_n: '{n} आइटम',
  item_1: '1 आइटम',
  added_toast: '{name} जोड़ा गया',
  order_placed_toast: 'ऑर्डर #{n} पूरा · {total}',
  offer_set_toast: '{name}: {pct}% ऑफर लगा',
  offer_cleared_toast: '{name}: ऑफर हटाया',
  all_offers_cleared_toast: 'सभी ऑफर हटाए गए',
  cat_offer_toast: '{name}: सभी आइटम पर {pct}% लगा',
  table_cleared_toast: '{name} खाली किया',
  customer_saved_toast: 'ग्राहक विवरण सहेजा गया',

  cat_special: 'लक स्पेशल',
  cat_pavbhaji: 'पावभाजी',
  cat_pulao: 'पुलाव',
  cat_dosa: 'डोसा · पेपर',
  cat_rollcut: 'रोल कट डोसा',
  cat_premroll: 'स्पेशल रोल कट',
  cat_soup: 'सूप',
  cat_manchurian: 'मंचूरियन',
  cat_rice: 'राइस',
  cat_noodles: 'नूडल्स',
  cat_bhel: 'भेल',
  cat_chinese: 'प्रीमियम चाइनीज़',
  cat_drinks: 'कोल्ड ड्रिंक्स',
  tables_tab: 'टेबल',
  add_floor_table: 'टेबल जोड़ें',
  edit_floor_table: 'टेबल बदलें',
  table_id: 'टेबल नंबर या कोड',
  table_id_hint: 'नंबर "टेबल 4" जैसा दिखेगा। शब्द जैसा है वैसा रहेगा।',
  table_label: 'दिखाने का नाम',
  table_label_hint: 'खाली छोड़ें तो नंबर इस्तेमाल होगा।',
  seats: 'सीटें',
  table_kind: 'प्रकार',
  kind_dine_in: 'डाइन-इन',
  kind_parcel: 'पार्सल',
  delete_table_confirm: 'मिटाने के लिए फिर दबाएँ',
  id_required: 'टेबल नंबर या नाम लिखें।',
  id_taken: 'यह टेबल पहले से है।',
  restaurant: 'रेस्टोरेंट',
  brand_name: 'रेस्टोरेंट का नाम',
  brand_tagline: 'टैगलाइन',
  address: 'पता',
  gstin: 'GSTIN',
  discount_presets: 'तेज़ छूट',
  discount_presets_hint: 'कॉमा से अलग करें, जैसे 10, 20, 25',
  max_discount: 'अधिकतम छूट %',
  no_tables_yet: 'अभी कोई टेबल नहीं। ऑर्डर लेने के लिए एक जोड़ें।',
  print: 'प्रिंट',
  print_bill: 'बिल प्रिंट करें',
  printing: 'प्रिंट हो रहा है…',
  print_failed: 'इस डिवाइस पर प्रिंट नहीं हो सका',
  invoice: 'बिल',
  gst_note: 'कीमतों में GST शामिल है। GST अलग से नहीं लिया जाता।',
  invoice_thanks: 'धन्यवाद — फिर पधारें',
  period_day: 'आज',
  period_week: 'इस हफ़्ते',
  period_month: 'इस महीने',
  revenue_over_time: 'समय के साथ आमदनी',
  orders_in_period: 'ऑर्डर',
  items_in_period: 'बिके आइटम',
  avg_order: 'औसत ऑर्डर',
  no_sales_in_period: 'इस अवधि में अभी कुछ नहीं बिका',
  no_open_orders: 'कुछ बाकी नहीं',
  nav_admin: 'एडमिन',
  title_admin: 'एडमिन',
  tag_admin: 'रेस्टोरेंट, स्टाफ़ और भूमिकाएँ',
  admin_web_only: 'एडमिन वेब ऐप से चलता है।',
  admin_owner_only: 'सिर्फ़ मालिक रेस्टोरेंट और स्टाफ़ संभाल सकते हैं।',
  admin_admin_only: 'सिर्फ़ एडमिन रेस्टोरेंट और मालिक संभाल सकते हैं।',
  nav_staff: 'स्टाफ़',
  title_staff: 'स्टाफ़',
  tag_staff: 'इस रेस्टोरेंट के कर्मचारी',
  staff_web_only: 'स्टाफ़ वेब ऐप से संभाला जाता है।',
  staff_owner_only: 'सिर्फ़ मालिक कर्मचारी संभाल सकते हैं।',
  workers: 'कर्मचारी',
  add_worker: 'कर्मचारी जोड़ें',
  no_workers: 'अभी कोई कर्मचारी नहीं। एक जोड़ें और उन्हें पहला पासवर्ड दें।',
  owner_appointed_by_admin: 'मालिक एडमिन तय करते हैं।',
  restaurants: 'रेस्टोरेंट',
  add_restaurant: 'रेस्टोरेंट जोड़ें',
  edit_restaurant: 'रेस्टोरेंट बदलें',
  restaurant_name: 'रेस्टोरेंट का नाम',
  staff: 'स्टाफ़',
  add_staff: 'स्टाफ़ जोड़ें',
  edit_staff: 'स्टाफ़ बदलें',
  staff_email: 'ईमेल',
  staff_role: 'भूमिका',
  role_admin: 'एडमिन',
  role_owner: 'मालिक',
  role_worker: 'कर्मचारी',
  role_owner_hint: 'सब कुछ, स्टाफ़ और रेस्टोरेंट की जानकारी संभालना भी।',
  role_worker_hint: 'ऑर्डर और मेन्यू संभालते हैं। स्टाफ़ नहीं संभाल सकते।',
  invite_pending: 'पहले साइन-इन का इंतज़ार',
  email_required: 'ईमेल लिखें।',
  email_taken: 'यह ईमेल पहले से टीम में है।',
  remove_staff_confirm: 'हटाने के लिए फिर दबाएँ',
  switch_restaurant: 'रेस्टोरेंट बदलें',
  no_access: 'यह अकाउंट अभी किसी रेस्टोरेंट में नहीं है',
  no_access_hint: 'मालिक से कहें कि {email} को एडमिन → स्टाफ़ में जोड़ें।',
  confirm_password: 'पासवर्ड दोबारा',
  passwords_differ: 'दोनों पासवर्ड अलग हैं।',
  password_too_short: 'कम से कम 6 अक्षर लिखें।',

  forgot_password: 'पासवर्ड भूल गए?',
  forgot_password_hint:
    'मालिक से कहें कि एडमिन → स्टाफ़ में आपका नया पासवर्ड बना दें। वे तुरंत बता देंगे।',
  staff_password: 'पहला पासवर्ड',
  staff_password_hint:
    'यह आप बनाते हैं और उन्हें बताते हैं। वे इसी से साइन इन करेंगे, फिर ऐप उन्हें अपना पासवर्ड चुनने देगा।',
  staff_password_new: 'नया पासवर्ड',
  staff_password_reset_hint:
    'खाली छोड़ें तो पुराना पासवर्ड वैसा ही रहेगा। यहाँ कुछ लिखा तो वह तुरंत बदल जाएगा।',
  set_password: 'पासवर्ड बनाएँ',
  password_set_toast: 'नया पासवर्ड बन गया',
  staff_needs_online: 'स्टाफ़ जोड़ने या पासवर्ड बदलने के लिए इंटरनेट चाहिए।',
  staff_login_exists:
    'जुड़ गए। इस ईमेल का अकाउंट पहले से था, इसलिए उनका पुराना पासवर्ड ही चलेगा — आपने जो लिखा वह इस्तेमाल नहीं हुआ।',
  change_password_title: 'पासवर्ड बदलें',
  change_password_hint:
    'पहले अभी वाला पासवर्ड लिखें, फिर नया पासवर्ड दो बार। कोई ईमेल नहीं भेजा जाता।',
  current_password: 'अभी वाला पासवर्ड',
  current_password_required: 'अभी वाला पासवर्ड लिखें।',
  current_password_wrong: 'यह आपका अभी वाला पासवर्ड नहीं है।',
  password_unchanged: 'नया पासवर्ड अभी वाले जैसा ही है।',
  password_changed_toast: 'पासवर्ड बदल गया',
  signed_in_as: 'साइन इन',
  account_worker: 'कर्मचारी',
  nav_customers: 'ग्राहक',
  title_customers: 'ग्राहक',
  tag_customers: 'कौन आया, कब, और कितना खर्च किया',
  no_customers: 'अभी कोई ग्राहक जानकारी नहीं',
  no_customers_hint: 'ऑर्डर में नाम या फ़ोन लिखें, वे यहाँ दिखेंगे।',
  search_customers: 'नाम या फ़ोन खोजें…',
  visits: '{n} बार आए',
  visit_1: '1 बार आए',
  last_visit: 'पिछली बार',
  first_visit: 'पहली बार',
  total_spent: 'कुल खर्च',
  customer_orders: 'उनके ऑर्डर',
  from_date: 'से',
  to_date: 'तक',
  period_custom: 'तारीख़ चुनें',
  date_hint: 'YYYY-MM-DD',
  bad_date: 'YYYY-MM-DD लिखें, जैसे {example}।',
  last_7: 'पिछले 7 दिन',
  last_30: 'पिछले 30 दिन',
  apply_range: 'लागू करें',

  nav_more: 'और',
  title_more: 'और',
  tag_more: 'बाकी सब कुछ जो ऐप कर सकता है',
  // ---- money out (owner only) ----
  nav_expenses: 'खर्च',
  title_expenses: 'खर्च',
  tag_expenses: 'दुकान का कितना खर्च हो रहा है',
  exp_salary: 'स्टाफ वेतन',
  exp_rent: 'किराया',
  exp_material: 'कच्चा माल',
  exp_vegetables: 'सब्ज़ी',
  exp_utilities: 'गैस और बिजली',
  exp_maintenance: 'मरम्मत',
  exp_other: 'अन्य',
  spent_in_period: 'कुल खर्च',
  revenue_in_period: 'कुल कमाई',
  net_profit: 'शुद्ध लाभ',
  net_loss: 'शुद्ध घाटा',
  margin_pct: '{pct}% मुनाफ़ा',
  no_margin_yet: 'इस अवधि में कोई बिक्री नहीं',
  entries_in_period: 'एंट्री',
  spend_over_time: 'समय के हिसाब से खर्च',
  by_head: 'पैसा कहाँ गया',
  all_entries: 'सारी एंट्री',
  no_spend_in_period: 'इस अवधि में कुछ दर्ज नहीं हुआ',
  no_heads_yet: 'अभी कोई खर्च का मद नहीं',
  entries_word: 'एंट्री',
  entry_1: '1 एंट्री',
  uncategorised: 'बिना मद',
  add_expense: 'खर्च जोड़ें',
  edit_expense: 'खर्च बदलें',
  expense_amount: 'कितना दिया',
  expense_head: 'किस लिए',
  expense_payee: 'किसे दिया',
  expense_payee_hint: 'नाम, दुकान या व्यक्ति — जिससे बाद में पहचान हो जाए।',
  expense_note: 'नोट',
  expense_date: 'तारीख',
  expense_date_hint: 'जिस दिन पैसा गया, न कि जिस दिन लिखा।',
  expense_amount_required: 'रकम लिखें।',
  expense_head_required: 'मद चुनें।',
  expense_date_required: 'तारीख लिखें।',
  expense_saved_toast: '{amount} दर्ज हुआ',
  delete_expense_confirm: 'मिटाने के लिए फिर दबाएँ',
  manage_heads: 'खर्च के मद',
  manage_heads_hint: 'इनके नाम बदलें, या अपने जोड़ें',
  add_head: 'मद जोड़ें',
  edit_head: 'मद बदलें',
  head_name: 'नाम',
  delete_head_confirm: 'फिर दबाएँ — एंट्री रहेंगी',
  head_name_required: 'नाम लिखें।',
  head_name_taken: 'इस नाम का मद पहले से है।',
  expenses_owner_only: 'दुकान का खर्च सिर्फ़ मालिक देख सकते हैं।',
};

const gu: Record<StringKey, string> = {
  nav_dashboard: 'ડેશબોર્ડ',
  nav_menu: 'મેનુ',
  nav_order: 'નવો ઓર્ડર',
  nav_orders: 'ઓર્ડર',
  nav_offers: 'ઓફર',
  nav_analytics: 'રિપોર્ટ',

  title_dashboard: 'ડેશબોર્ડ',
  tag_dashboard: 'આજનો સારાંશ',
  title_menu: 'મેનુ વ્યવસ્થાપન',
  tag_menu: 'શ્રેણી પ્રમાણે આઇટમ જુઓ અને ઓફર લગાવો',
  title_order: 'નવો ઓર્ડર',
  tag_order: 'પહેલાં ટેબલ પસંદ કરો, પછી આઇટમ ઉમેરો',
  title_orders: 'ઓર્ડર ઇતિહાસ',
  tag_orders: 'આ સત્રના બધા ઓર્ડર',
  title_offers: 'ઓફર',
  tag_offers: 'આઇટમ કે આખી શ્રેણી પર છૂટ આપો',
  title_analytics: 'રિપોર્ટ',
  tag_analytics: 'સૌથી વધુ શું વેચાય છે',

  stat_items: 'મેનુ આઇટમ',
  stat_categories: 'શ્રેણીઓ',
  stat_on_offer: 'ઓફરવાળા આઇટમ',
  stat_open_tables: 'ચાલુ ટેબલ',
  stat_orders: 'કુલ ઓર્ડર',
  stat_revenue: 'કુલ આવક',
  top_sellers: 'સૌથી વધુ વેચાતા',
  top_sellers_hint: 'ઓર્ડર કરો અને અહીં ફેરફાર જુઓ',
  recent_orders: 'તાજેતરના ઓર્ડર',
  running_tables: 'ચાલુ ટેબલ',
  no_orders_yet: 'હજુ સુધી કોઈ ઓર્ડર નથી.',
  no_running_tables: 'અત્યારે કોઈ ટેબલ ચાલુ નથી.',
  no_sales_yet: 'હજુ કોઈ વેચાણ નથી — એક ઓર્ડર કરો.',

  step_table: 'ટેબલ',
  step_category: 'શ્રેણી',
  step_items: 'આઇટમ',
  select_table: 'ટેબલ પસંદ કરો',
  select_table_hint: 'દરેક ટેબલનો પોતાનો ઓર્ડર અલગ ચાલે છે',
  table: 'ટેબલ',
  table_free: 'ખાલી',
  parcel: 'પાર્સલ',
  add_table: 'ટેબલ ઉમેરો',
  add_table_title: 'ટેબલ નંબર ઉમેરો',
  add_table_hint: 'કોઈપણ ટેબલ નંબર કે ટૂંકું નામ લખો.',
  select_category: 'શ્રેણી પસંદ કરો',
  select_category_hint: 'આઇટમ જોવા શ્રેણી પર દબાવો',
  back: 'પાછળ',
  view_cart: 'ઓર્ડર જુઓ',
  cart: 'હાલનો ઓર્ડર',
  empty_cart: 'હજુ કંઈ ઉમેર્યું નથી. પાછળ જઈને શ્રેણી પસંદ કરો.',
  place_order: 'ઓર્ડર કરો',
  clear_table: 'ટેબલ ખાલી કરો',
  customer_details: 'ગ્રાહકની વિગત',
  optional: 'વૈકલ્પિક',
  customer_name: 'નામ',
  customer_phone: 'ફોન નંબર',
  walk_in: 'નામ વગર',
  items_in_order: 'ઓર્ડરમાં',

  subtotal: 'પેટા સરવાળો',
  discount: 'છૂટ',
  item_savings: 'આઇટમ ઓફરથી બચત',
  total: 'કુલ',
  rate: 'ભાવ',
  qty_short: 'નંગ',
  amount: 'રકમ',

  category_offer_title: 'આખી શ્રેણી પર ઓફર લગાવો',
  category_offer_hint: 'ઓછી ભીડના સમય માટે સરસ',
  items_on_offer: 'અત્યારે ઓફરવાળા આઇટમ',
  clear_all_offers: 'બધી ઓફર કાઢો',
  no_offers: 'કોઈ ઓફર ચાલુ નથી. ઉપરથી કે મેનુ ટેબમાંથી લગાવો.',
  off: 'છૂટ',
  custom: 'પોતાની',
  clear: 'કાઢો',
  custom_discount_for: '"{name}" માટે પોતાની છૂટ',
  discount_range_hint: '0 થી {max} વચ્ચે છૂટ લખો.',
  cancel: 'રદ કરો',
  apply: 'લગાવો',
  save: 'સાચવો',

  order_no: 'ઓર્ડર #{n}',
  order_details: 'ઓર્ડરની વિગત',
  no_history: 'આ સત્રમાં હજુ કોઈ ઓર્ડર નથી.',
  placed_at: 'સમય',
  items_label: 'આઇટમ',
  close: 'બંધ કરો',
  base_price: 'મેનુ ભાવ',

  best_selling: 'સૌથી વધુ વેચાતા આઇટમ',
  by_category: 'શ્રેણી પ્રમાણે',
  no_analytics: 'થોડા ઓર્ડર કરો, પછી અહીં ક્રમ દેખાશે.',
  orders_word: 'ઓર્ડર',
  items_on_menu: 'આઇટમ મેનુમાં',

  search_placeholder: 'મેનુ આઇટમ શોધો…',
  no_match: 'કોઈ આઇટમ મળ્યું નથી.',
  language: 'ભાષા',
  text_size: 'અક્ષરનું કદ',
  settings: 'સેટિંગ',
  appearance: 'દેખાવ',
  size_sm: 'નાનું',
  size_md: 'સામાન્ય',
  size_lg: 'મોટું',
  size_xl: 'ખૂબ મોટું',
  size_preview: 'પનીર ચિલી ડ્રાય · ₹199',
  sign_in: 'સાઇન ઇન',
  sign_out: 'સાઇન આઉટ',
  email: 'ઈમેલ',
  password: 'પાસવર્ડ',
  signing_in: 'સાઇન ઇન થાય છે…',
  sign_in_subtitle: 'સાઇન ઇન કરો જેથી આ ડિવાઇસ બીજા સાથે ઓર્ડર શેર કરે.',
  work_offline: 'આ ડિવાઇસ પર ઓફલાઇન ચલાવો',
  sync_synced: 'બધું સેવ છે',
  sync_syncing: 'સેવ થાય છે…',
  sync_offline: 'ઓફલાઇન',
  sync_error: 'સેવ થયું નથી',
  sync_signed_out: 'સાઇન ઇન નથી',
  sync_no_access: 'પ્રવેશ નથી',
  sync_pending: '{n} બાકી',
  sync_now: 'હમણાં સિંક કરો',
  not_synced: 'હજુ અપલોડ થયું નથી',
  account: 'ખાતું',
  browse: 'જુઓ',
  manage: 'વ્યવસ્થા',
  add_category: 'શ્રેણી ઉમેરો',
  edit_category: 'શ્રેણી બદલો',
  add_item: 'આઇટમ ઉમેરો',
  edit_item: 'આઇટમ બદલો',
  item_name: 'આઇટમનું નામ',
  category_name: 'શ્રેણીનું નામ',
  name_en: 'નામ (અંગ્રેજી)',
  name_hi: 'નામ (હિન્દી)',
  name_gu: 'નામ (ગુજરાતી)',
  icon: 'આઇકન',
  prices_label: 'ભાવ',
  variant_label: 'નામ',
  price_label: 'ભાવ',
  add_variant: 'ભાવ વિકલ્પ ઉમેરો',
  variant_hint: 'એક જ ભાવ હોય તો "Price" રાખો, અથવા Half / Full, Oil / Butter ઉમેરો.',
  delete: 'કાઢો',
  delete_category_confirm: 'આ શ્રેણી અને તેના બધા આઇટમ કાઢી નાખવા?',
  delete_item_confirm: 'આ આઇટમ કાઢી નાખવું?',
  active_label: 'ઉપલબ્ધ',
  hidden_label: 'છુપાયેલ',
  no_items_here: 'આ શ્રેણીમાં હજુ કોઈ આઇટમ નથી.',
  saved_toast: 'સાચવ્યું',
  deleted_toast: 'કાઢી નાખ્યું',
  name_required: 'નામ લખો',
  price_required: 'ઓછામાં ઓછો એક ભાવ ઉમેરો',
  theme: 'દેખાવ',
  theme_light: 'આછું',
  theme_dark: 'ઘાટું',
  all: 'બધા',
  items_n: '{n} આઇટમ',
  item_1: '1 આઇટમ',
  added_toast: '{name} ઉમેર્યું',
  order_placed_toast: 'ઓર્ડર #{n} થયો · {total}',
  offer_set_toast: '{name}: {pct}% ઓફર લાગી',
  offer_cleared_toast: '{name}: ઓફર કાઢી',
  all_offers_cleared_toast: 'બધી ઓફર કાઢી નાખી',
  cat_offer_toast: '{name}: બધા આઇટમ પર {pct}% લાગ્યું',
  table_cleared_toast: '{name} ખાલી કર્યું',
  customer_saved_toast: 'ગ્રાહકની વિગત સાચવી',

  cat_special: 'લક સ્પેશિયલ',
  cat_pavbhaji: 'પાવભાજી',
  cat_pulao: 'પુલાવ',
  cat_dosa: 'ડોસા · પેપર',
  cat_rollcut: 'રોલ કટ ડોસા',
  cat_premroll: 'સ્પેશિયલ રોલ કટ',
  cat_soup: 'સૂપ',
  cat_manchurian: 'મંચુરિયન',
  cat_rice: 'રાઇસ',
  cat_noodles: 'નૂડલ્સ',
  cat_bhel: 'ભેલ',
  cat_chinese: 'પ્રીમિયમ ચાઇનીઝ',
  cat_drinks: 'કોલ્ડ ડ્રિંક્સ',
  tables_tab: 'ટેબલ',
  add_floor_table: 'ટેબલ ઉમેરો',
  edit_floor_table: 'ટેબલ બદલો',
  table_id: 'ટેબલ નંબર અથવા કોડ',
  table_id_hint: 'નંબર "ટેબલ 4" જેવો દેખાશે. શબ્દ જેમ છે તેમ રહેશે.',
  table_label: 'બતાવવાનું નામ',
  table_label_hint: 'ખાલી છોડો તો નંબર વપરાશે.',
  seats: 'બેઠકો',
  table_kind: 'પ્રકાર',
  kind_dine_in: 'ડાઇન-ઇન',
  kind_parcel: 'પાર્સલ',
  delete_table_confirm: 'કાઢવા માટે ફરી દબાવો',
  id_required: 'ટેબલ નંબર અથવા નામ લખો.',
  id_taken: 'આ ટેબલ પહેલેથી છે.',
  restaurant: 'રેસ્ટોરન્ટ',
  brand_name: 'રેસ્ટોરન્ટનું નામ',
  brand_tagline: 'ટૅગલાઇન',
  address: 'સરનામું',
  gstin: 'GSTIN',
  discount_presets: 'ઝડપી છૂટ',
  discount_presets_hint: 'કોમાથી અલગ કરો, જેમ કે 10, 20, 25',
  max_discount: 'મહત્તમ છૂટ %',
  no_tables_yet: 'હજી કોઈ ટેબલ નથી. ઓર્ડર લેવા એક ઉમેરો.',
  print: 'પ્રિન્ટ',
  print_bill: 'બિલ પ્રિન્ટ કરો',
  printing: 'પ્રિન્ટ થાય છે…',
  print_failed: 'આ ડિવાઇસ પર પ્રિન્ટ ન થઈ શક્યું',
  invoice: 'બિલ',
  gst_note: 'આ ભાવ GST સાથેના છે. GST અલગ લેવામાં આવતો નથી.',
  invoice_thanks: 'આભાર — ફરી પધારજો',
  period_day: 'આજે',
  period_week: 'આ અઠવાડિયે',
  period_month: 'આ મહિને',
  revenue_over_time: 'સમય સાથે આવક',
  orders_in_period: 'ઓર્ડર',
  items_in_period: 'વેચાયેલી આઇટમ',
  avg_order: 'સરેરાશ ઓર્ડર',
  no_sales_in_period: 'આ સમયગાળામાં હજી કંઈ વેચાયું નથી',
  no_open_orders: 'કંઈ બાકી નથી',
  nav_admin: 'એડમિન',
  title_admin: 'એડમિન',
  tag_admin: 'રેસ્ટોરન્ટ, સ્ટાફ અને ભૂમિકા',
  admin_web_only: 'એડમિન વેબ એપથી ચાલે છે.',
  admin_owner_only: 'ફક્ત માલિક રેસ્ટોરન્ટ અને સ્ટાફ સંભાળી શકે.',
  admin_admin_only: 'ફક્ત એડમિન રેસ્ટોરન્ટ અને માલિક સંભાળી શકે.',
  nav_staff: 'સ્ટાફ',
  title_staff: 'સ્ટાફ',
  tag_staff: 'આ રેસ્ટોરન્ટના કર્મચારી',
  staff_web_only: 'સ્ટાફ વેબ એપથી સંભાળાય છે.',
  staff_owner_only: 'ફક્ત માલિક કર્મચારી સંભાળી શકે.',
  workers: 'કર્મચારી',
  add_worker: 'કર્મચારી ઉમેરો',
  no_workers: 'હજી કોઈ કર્મચારી નથી. એક ઉમેરો અને તેમને પહેલો પાસવર્ડ આપો.',
  owner_appointed_by_admin: 'માલિકની નિમણૂક એડમિન કરે છે.',
  restaurants: 'રેસ્ટોરન્ટ',
  add_restaurant: 'રેસ્ટોરન્ટ ઉમેરો',
  edit_restaurant: 'રેસ્ટોરન્ટ બદલો',
  restaurant_name: 'રેસ્ટોરન્ટનું નામ',
  staff: 'સ્ટાફ',
  add_staff: 'સ્ટાફ ઉમેરો',
  edit_staff: 'સ્ટાફ બદલો',
  staff_email: 'ઈમેલ',
  staff_role: 'ભૂમિકા',
  role_admin: 'એડમિન',
  role_owner: 'માલિક',
  role_worker: 'કર્મચારી',
  role_owner_hint: 'બધું જ, સ્ટાફ અને રેસ્ટોરન્ટની વિગતો સંભાળવા સહિત.',
  role_worker_hint: 'ઓર્ડર અને મેનુ સંભાળે. સ્ટાફ ન સંભાળી શકે.',
  invite_pending: 'પહેલા સાઇન-ઇનની રાહ',
  email_required: 'ઈમેલ લખો.',
  email_taken: 'આ ઈમેલ પહેલેથી ટીમમાં છે.',
  remove_staff_confirm: 'કાઢવા માટે ફરી દબાવો',
  switch_restaurant: 'રેસ્ટોરન્ટ બદલો',
  no_access: 'આ એકાઉન્ટ હજી કોઈ રેસ્ટોરન્ટમાં નથી',
  no_access_hint: 'માલિકને કહો કે {email} ને એડમિન → સ્ટાફમાં ઉમેરે.',
  confirm_password: 'પાસવર્ડ ફરીથી',
  passwords_differ: 'બંને પાસવર્ડ અલગ છે.',
  password_too_short: 'ઓછામાં ઓછા 6 અક્ષર લખો.',

  forgot_password: 'પાસવર્ડ ભૂલી ગયા?',
  forgot_password_hint:
    'માલિકને કહો કે એડમિન → સ્ટાફમાં તમારો નવો પાસવર્ડ બનાવી આપે. તેઓ તરત જ કહી શકશે.',
  staff_password: 'પહેલો પાસવર્ડ',
  staff_password_hint:
    'આ તમે બનાવો છો અને તેમને કહો છો. તેઓ આનાથી સાઇન ઇન કરશે, પછી ઍપ તેમને પોતાનો પાસવર્ડ પસંદ કરવા દેશે.',
  staff_password_new: 'નવો પાસવર્ડ',
  staff_password_reset_hint:
    'ખાલી છોડો તો જૂનો પાસવર્ડ એમ જ રહેશે. અહીં કંઈ લખશો તો તે તરત બદલાઈ જશે.',
  set_password: 'પાસવર્ડ સેટ કરો',
  password_set_toast: 'નવો પાસવર્ડ સેટ થયો',
  staff_needs_online: 'સ્ટાફ ઉમેરવા કે પાસવર્ડ બદલવા ઇન્ટરનેટ જોઈએ.',
  staff_login_exists:
    'ઉમેરાયા. આ ઈમેલનું એકાઉન્ટ પહેલેથી હતું, એટલે તેમનો જૂનો પાસવર્ડ જ ચાલશે — તમે લખેલો વપરાયો નથી.',
  change_password_title: 'પાસવર્ડ બદલો',
  change_password_hint:
    'પહેલાં અત્યારનો પાસવર્ડ લખો, પછી નવો પાસવર્ડ બે વાર. કોઈ ઈમેલ મોકલાતો નથી.',
  current_password: 'અત્યારનો પાસવર્ડ',
  current_password_required: 'અત્યારનો પાસવર્ડ લખો.',
  current_password_wrong: 'આ તમારો અત્યારનો પાસવર્ડ નથી.',
  password_unchanged: 'નવો પાસવર્ડ અત્યારના જેવો જ છે.',
  password_changed_toast: 'પાસવર્ડ બદલાયો',
  signed_in_as: 'સાઇન ઇન',
  account_worker: 'કર્મચારી',
  nav_customers: 'ગ્રાહકો',
  title_customers: 'ગ્રાહકો',
  tag_customers: 'કોણ આવ્યું, ક્યારે, અને કેટલો ખર્ચ',
  no_customers: 'હજી કોઈ ગ્રાહક માહિતી નથી',
  no_customers_hint: 'ઓર્ડરમાં નામ કે ફોન લખો, તે અહીં દેખાશે.',
  search_customers: 'નામ કે ફોન શોધો…',
  visits: '{n} વાર આવ્યા',
  visit_1: '1 વાર આવ્યા',
  last_visit: 'છેલ્લી વાર',
  first_visit: 'પહેલી વાર',
  total_spent: 'કુલ ખર્ચ',
  customer_orders: 'તેમના ઓર્ડર',
  from_date: 'થી',
  to_date: 'સુધી',
  period_custom: 'તારીખ પસંદ કરો',
  date_hint: 'YYYY-MM-DD',
  bad_date: 'YYYY-MM-DD લખો, જેમ કે {example}.',
  last_7: 'છેલ્લા 7 દિવસ',
  last_30: 'છેલ્લા 30 દિવસ',
  apply_range: 'લાગુ કરો',

  nav_more: 'વધુ',
  title_more: 'વધુ',
  tag_more: 'બાકીનું બધું જે ઍપ કરી શકે',
  // ---- money out (owner only) ----
  nav_expenses: 'ખર્ચ',
  title_expenses: 'ખર્ચ',
  tag_expenses: 'દુકાનનો કેટલો ખર્ચ થાય છે',
  exp_salary: 'સ્ટાફ પગાર',
  exp_rent: 'ભાડું',
  exp_material: 'કાચો માલ',
  exp_vegetables: 'શાકભાજી',
  exp_utilities: 'ગેસ અને વીજળી',
  exp_maintenance: 'મરામત',
  exp_other: 'અન્ય',
  spent_in_period: 'કુલ ખર્ચ',
  revenue_in_period: 'કુલ આવક',
  net_profit: 'ચોખ્ખો નફો',
  net_loss: 'ચોખ્ખી ખોટ',
  margin_pct: '{pct}% નફો',
  no_margin_yet: 'આ સમયગાળામાં કોઈ વેચાણ નથી',
  entries_in_period: 'એન્ટ્રી',
  spend_over_time: 'સમય પ્રમાણે ખર્ચ',
  by_head: 'પૈસા ક્યાં ગયા',
  all_entries: 'બધી એન્ટ્રી',
  no_spend_in_period: 'આ સમયગાળામાં કશું નોંધાયું નથી',
  no_heads_yet: 'હજી કોઈ ખર્ચની બાબત નથી',
  entries_word: 'એન્ટ્રી',
  entry_1: '1 એન્ટ્રી',
  uncategorised: 'બાબત વગર',
  add_expense: 'ખર્ચ ઉમેરો',
  edit_expense: 'ખર્ચ બદલો',
  expense_amount: 'કેટલા ચૂકવ્યા',
  expense_head: 'શેના માટે',
  expense_payee: 'કોને ચૂકવ્યા',
  expense_payee_hint: 'નામ, દુકાન કે વ્યક્તિ — જેથી પછી ઓળખાય.',
  expense_note: 'નોંધ',
  expense_date: 'તારીખ',
  expense_date_hint: 'જે દિવસે પૈસા ગયા, લખ્યા તે દિવસે નહીં.',
  expense_amount_required: 'રકમ લખો.',
  expense_head_required: 'બાબત પસંદ કરો.',
  expense_date_required: 'તારીખ લખો.',
  expense_saved_toast: '{amount} નોંધાયું',
  delete_expense_confirm: 'કાઢવા ફરી દબાવો',
  manage_heads: 'ખર્ચની બાબતો',
  manage_heads_hint: 'નામ બદલો, કે તમારી ઉમેરો',
  add_head: 'બાબત ઉમેરો',
  edit_head: 'બાબત બદલો',
  head_name: 'નામ',
  delete_head_confirm: 'ફરી દબાવો — એન્ટ્રી રહેશે',
  head_name_required: 'નામ લખો.',
  head_name_taken: 'આ નામની બાબત પહેલેથી છે.',
  expenses_owner_only: 'દુકાનનો ખર્ચ ફક્ત માલિક જોઈ શકે.',
};

export const STRINGS: Record<Lang, Record<StringKey, string>> = { en, hi, gu };

export type Vars = Record<string, string | number>;

/** Looks up a string and substitutes {placeholders}. Falls back to English. */
export function translate(lang: Lang, key: StringKey, vars?: Vars): string {
  const raw = STRINGS[lang]?.[key] ?? STRINGS.en[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (m, name) =>
    name in vars ? String(vars[name]) : m,
  );
}

/** "1 item" / "5 items", picking the right form per language. */
export function itemsLabel(lang: Lang, n: number): string {
  return n === 1 ? translate(lang, 'item_1') : translate(lang, 'items_n', { n });
}

